import React, { useState, useEffect } from 'react';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Package, ChevronDown, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatPrice, formatQty } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyOrders = () => {
  const { user } = useOutletContext();
  const [searchParams] = useSearchParams();
  const projectIdParam = searchParams.get('project_id');

  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [orderItems, setOrderItems] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(projectIdParam || 'ALL');

  useEffect(() => {
    fetchData();
  }, [selectedProject]);

  const fetchData = async () => {
    try {
      const projectsRes = await axios.get(`${BACKEND_URL}/api/projects`, { withCredentials: true });
      setProjects(projectsRes.data);

      const ordersUrl = selectedProject && selectedProject !== 'ALL'
        ? `${BACKEND_URL}/api/orders?project_id=${selectedProject}`
        : `${BACKEND_URL}/api/orders`;
      const ordersRes = await axios.get(ordersUrl, { withCredentials: true });
      setOrders(ordersRes.data);

      const itemsData = {};
      const expandedState = {};
      for (const order of ordersRes.data) {
        try {
          const itemsRes = await axios.get(`${BACKEND_URL}/api/order-items/${order.order_id}`, { withCredentials: true });
          itemsData[order.order_id] = itemsRes.data;
          expandedState[order.order_id] = true;
        } catch (error) {
          itemsData[order.order_id] = [];
          expandedState[order.order_id] = true;
        }
      }
      setOrderItems(itemsData);
      setExpandedOrders(expandedState);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.project_name || 'Unknown';
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Open': 'bg-stone-100 text-stone-800 border-stone-200',
      'Partially Delivered': 'bg-amber-100 text-amber-800 border-amber-200',
      'Completed': 'bg-lime-100 text-lime-800 border-lime-200'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${styles[status] || styles['Open']}`}>
        {status}
      </span>
    );
  };

  const getItemStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-stone-100 text-stone-800',
      'Delivered': 'bg-lime-100 text-lime-800',
      'No Stock': 'bg-red-100 text-red-800',
      'Ordered from Vendor': 'bg-amber-100 text-amber-800',
      'Dispatched from Vendor': 'bg-amber-100 text-amber-800',
      'Received in Store': 'bg-lime-100 text-lime-800',
      'Ready for Delivery': 'bg-lime-100 text-lime-800'
    };
    return (
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[status] || colors['Pending']}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="text-stone-600">Loading your orders...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            My Orders
          </h1>
          <p className="mt-2 text-base text-stone-600">Track the status of your orders and deliveries</p>
        </div>
        
        {projects.length > 1 && (
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-64 border-stone-200" data-testid="project-filter">
              <SelectValue placeholder="Filter by Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Projects</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.project_id} value={project.project_id}>
                  {project.project_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-lg border border-stone-200 p-12 text-center">
          <Package className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-medium text-stone-900 mb-2">No Orders Yet</h3>
          <p className="text-stone-600">Your orders will appear here once they are placed.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700 w-12"></th>
                  <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Order ID</th>
                  <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Project</th>
                  <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Order Date</th>
                  <th className="text-right px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Total Amount</th>
                  <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <React.Fragment key={order.order_id}>
                    <tr data-testid={`order-row-${order.order_id}`} className="hover:bg-stone-50 transition-colors duration-200 border-b border-stone-200">
                      <td className="px-6 py-4">
                        <button
                          data-testid={`toggle-order-${order.order_id}`}
                          onClick={() => toggleOrder(order.order_id)}
                          className="text-stone-700 hover:text-amber-700 transition-colors duration-200"
                        >
                          {expandedOrders[order.order_id] ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 font-medium text-stone-900">{order.order_id}</td>
                      <td className="px-6 py-4 text-stone-700">{getProjectName(order.project_id)}</td>
                      <td className="px-6 py-4 text-stone-700 tabular-nums">{order.order_date}</td>
                      <td className="px-6 py-4 text-right text-stone-900 font-medium tabular-nums">₹{formatPrice(order.total_amount)}</td>
                      <td className="px-6 py-4">{getStatusBadge(order.status)}</td>
                    </tr>
                    {expandedOrders[order.order_id] && (
                      <tr>
                        <td colSpan="6" className="bg-stone-50 px-6 py-4">
                          <div className="space-y-2">
                            <h3 className="text-sm font-semibold text-stone-900 mb-3">Order Items</h3>
                            {orderItems[order.order_id]?.length === 0 ? (
                              <p className="text-sm text-stone-500">No items in this order</p>
                            ) : (
                              <div className="bg-white rounded-md border border-stone-200 overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-stone-100 border-b border-stone-200">
                                    <tr>
                                      <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-stone-700">Product</th>
                                      <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-medium text-stone-700">Quantity</th>
                                      <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-medium text-stone-700">Amount</th>
                                      <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-stone-700">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-stone-200">
                                    {orderItems[order.order_id]?.map((item) => (
                                      <tr key={item.order_item_id} className="hover:bg-stone-50">
                                        <td className="px-4 py-3">
                                          <div className="font-medium text-stone-900">{item.product_name} - {item.product_brand}</div>
                                          <div className="text-xs text-stone-500">{item.product_size} - {item.product_color}</div>
                                        </td>
                                        <td className="px-4 py-3 text-right text-stone-900 tabular-nums">{formatQty(item.quantity)}</td>
                                        <td className="px-4 py-3 text-right text-stone-900 font-medium tabular-nums">₹{formatPrice(item.price)}</td>
                                        <td className="px-4 py-3">{getItemStatusBadge(item.status)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;
